import React, { useState, useEffect, useMemo, useId, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FrostedPanel from "../../molecules/FrostedPanel";
import UserCardAvatar from "../../molecules/UserCardAvatar";
import IconButton from "../../atoms/IconButton";
import CardActionsMenu from "../../molecules/CardActionsMenu";
import PostVisibilityControl from "../../molecules/PostVisibilityControl";
import { BAND_POST_VISIBILITY_OPTIONS } from "../../molecules/PostVisibilityControl/options";
import { useClickableLinks } from "../../../hooks/useClickableLinks";
import { postsService } from "../../../services/api/posts";
import classNames from "../../../utils/classNames";
import PostLinkPreview from "../../molecules/PostLinkPreview";
import ContentWithLinkPreviews from "../../molecules/ContentWithLinkPreviews";
import PostEngagement from "../../molecules/PostEngagement";
import CommentPreview from "../../molecules/CommentPreview";
import PostComments from "../../molecules/PostComments";
import LikeButton from "../../atoms/LikeButton";
import CommentToggle from "../../atoms/CommentToggle";
import { PostSaveIcon, PostCancelIcon } from "../../atoms/Iconography";
import useImageLightbox from "../../../hooks/useImageLightbox";
import { attachmentsToItems } from "../../../utils/imageUtils";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import { extractPrimaryLinkFromText } from "../../../utils/linkDetection";
import { normalizePostBody } from "../../../utils/postBody";
import { toBandPostUiVisibility, toUiVisibility } from "../../../utils/visibility";
import useLocaleDateFormatter from "../../../hooks/useLocaleDateFormatter";
import { isSupportedLinkPreviewUrl } from "../../molecules/LinkWithPreview/supportedLinkPreview.ts";
import { formatLastSeenRelative } from "../../../utils/presenceUtils";
import { resolveUserAvatarUrl, resolveUserDisplayName, resolveUserUsername } from "../../../utils/userIdentity";
import { trimToRecentThreads } from "../../../utils/commentThreads";
import "./index.scss";

const COMMENT_PAGE_SIZE = 50;

export { trimToRecentThreads };

export default function PostCard({
    post,
    currentUserId,
    dropdownOpenId,
    onToggleDropdown,
    onDelete,
    onPostMetaChange,
    onEditPost,
    defaultShowComments = false,
    editorialEngagement = false,
    readOnly = false,
    hideActionsMenu = false,
}) {
    const { t } = useTranslation();
    const { formatDateTime, formatDate, formatTime } = useLocaleDateFormatter();
    const renderPostBodyWithLinks = useClickableLinks();
    const isReadOnly = readOnly === true;
    const ownerId = post?.userId != null ? String(post.userId) : null;
    const viewerId = currentUserId != null ? String(currentUserId) : null;
    const isOwner = !isReadOnly && (post?.ownedByViewer ?? (ownerId && viewerId && ownerId === viewerId));
    const canManagePost = !isReadOnly && post?.canManage === true;
    const canReact = !isReadOnly && post?.canReact !== false;
    const canComment = !isReadOnly && post?.canComment !== false;
    const isBandPost = String(post?.relatedObject?.objectType || '').trim().toUpperCase() === 'BAND';

    const [reactions, setReactions] = useState(post.reactions || []);
    const [comments, setComments] = useState((post.recentComments || []).slice());
    const [commentsCount, setCommentsCount] = useState(post.commentsCount || 0);
    const [commentsExpanded, setCommentsExpanded] = useState(false);
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [commentInput, setCommentInput] = useState("");
    const [replyTo, setReplyTo] = useState(null);
    const [showComments, setShowComments] = useState(Boolean(defaultShowComments));
    const [isVisibilityMenuOpen, setVisibilityMenuOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const normalizedBody = useMemo(() => normalizePostBody(post?.body), [post?.body]);
    const [draftBody, setDraftBody] = useState(normalizedBody);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const { lightboxEl, openWithItems } = useImageLightbox({ tone: 'dark' });
    const editableFieldId = useId();
    const commentComposerRef = useRef(null);
    const renderedBody = useMemo(() => renderPostBodyWithLinks(normalizedBody), [normalizedBody, renderPostBodyWithLinks]);

    const isMenuOpen = dropdownOpenId === post.id;
    const cardClassName = classNames("post-card", {
        "post-card--menu-open": isMenuOpen || isVisibilityMenuOpen,
        "post-card--editing": isEditing,
    });

    useEffect(() => {
        setReactions(post.reactions || []);
        if (!commentsExpanded) {
            setComments((post.recentComments || []).slice());
        }
        setCommentsCount(post.commentsCount || 0);
    }, [post.id, post.reactions, post.recentComments, post.commentsCount, commentsExpanded]);

    // Reset transient comment-panel state when the rendered post genuinely
    // changes (a reused PostCard instance switching to a different post).
    // Skip the first run so an initial `defaultShowComments={true}` survives
    // mount — without this guard the panel collapses immediately on the first
    // render after we initialized it open.
    const previousPostIdRef = useRef(post.id);
    useEffect(() => {
        if (previousPostIdRef.current === post.id) {
            return;
        }
        previousPostIdRef.current = post.id;
        setShowComments(Boolean(defaultShowComments));
        setCommentsExpanded(false);
        setCommentInput("");
        setReplyTo(null);
    }, [post.id, defaultShowComments]);

    useEffect(() => {
        if (!isEditing) {
            setDraftBody(normalizedBody);
        }
    }, [normalizedBody, isEditing]);

    useEffect(() => {
        setIsEditing(false);
        setIsSavingEdit(false);
        setVisibilityMenuOpen(false);
    }, [post.id]);

    const visibilityValue = useMemo(
        () => (
            isBandPost
                ? toBandPostUiVisibility(post?.visibility || post?.visibilityLabel)
                : toUiVisibility(post?.visibility || post?.visibilityLabel)
        ),
        [isBandPost, post?.visibility, post?.visibilityLabel],
    );

    const displayName = resolveUserDisplayName(post?.author, post?.title || "");
    const avatarUrl = resolveUserAvatarUrl(post?.author);

    const imageAttachments = useMemo(
        () => {
            const raw = Array.isArray(post?.attachments) ? post.attachments : [];
            return raw
                .filter((attachment) => attachment
                    && String(attachment.type || 'IMAGE').toUpperCase() === 'IMAGE'
                    && attachment.url)
                .map((attachment) => ({
                    ...attachment,
                    displayUrl: resolveMediaUrl(attachment.url),
                }));
        },
        [post?.attachments]
    );
    const attachmentItems = imageAttachments.length ? attachmentsToItems(imageAttachments) : [];

    const formattedTimestamp = useMemo(
        () => formatDateTime(post?.createdAt, {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }),
        [formatDateTime, post?.createdAt]
    );
    const compactTimestamp = useMemo(() => {
        if (!post?.createdAt) {
            return "";
        }
        const datePart = formatDate(post.createdAt, { year: undefined, month: "short" });
        const timePart = formatTime(post.createdAt);
        if (datePart && timePart) {
            return `${datePart} · ${timePart}`;
        }
        return datePart || timePart || "";
    }, [formatDate, formatTime, post?.createdAt]);
    const relativeTimestamp = useMemo(
        () => (post?.createdAt ? formatLastSeenRelative(post.createdAt) : ""),
        [post?.createdAt]
    );
    const timestampLabel = useMemo(() => (
        formattedTimestamp ? t("home.postCard.timestamp", { timestamp: formattedTimestamp }) : ""
    ), [formattedTimestamp, t]);

    const effectiveBody = isEditing ? draftBody : normalizedBody;
    const primaryLink = useMemo(() => extractPrimaryLinkFromText(effectiveBody), [effectiveBody]);
    const shouldRenderPrimaryLinkCard = useMemo(
        () => Boolean(primaryLink) && !isSupportedLinkPreviewUrl(primaryLink),
        [primaryLink],
    );
    const trimmedDraft = draftBody.trim();
    const originalTrimmed = (normalizedBody || "").trim();
    const canSaveEdit = trimmedDraft.length > 0 && trimmedDraft !== originalTrimmed;

    const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
    const canToggleComments = canComment || commentsCount > 0 || comments.length > 0;

    const totalComments = useMemo(() => {
        const inferredCount = typeof commentsCount === "number" ? commentsCount : 0;
        if (inferredCount > 0) {
            return inferredCount;
        }
        if (comments.length > 0) {
            return comments.length;
        }
        if (Array.isArray(post?.recentComments)) {
            return post.recentComments.length;
        }
        return 0;
    }, [comments, commentsCount, post?.recentComments]);

    const latestComment = useMemo(() => {
        if (comments.length > 0) {
            return comments[comments.length - 1];
        }
        const recent = Array.isArray(post?.recentComments) ? post.recentComments : [];
        if (recent.length === 0) {
            return null;
        }
        return recent[recent.length - 1];
    }, [comments, post?.recentComments]);

    const latestCommentCreatedAt = latestComment?.createdAt;

    const latestCommentAuthorName = useMemo(() => {
        if (!latestComment) {
            return "";
        }
        return resolveUserDisplayName(latestComment.author, t("home.postCard.comments.anonymousAuthor"));
    }, [latestComment, t]);

    const latestCommentAuthorId = useMemo(() => {
        if (!latestComment) {
            return null;
        }
        const authorRef = latestComment.author || {};
        return authorRef.id
            || authorRef.userId
            || latestComment.authorId
            || latestComment.userId
            || null;
    }, [latestComment]);

    const latestCommentAvatarUrl = useMemo(() => {
        const rawAvatarUrl = resolveUserAvatarUrl(latestComment?.author);
        if (!rawAvatarUrl) {
            return null;
        }
        return resolveMediaUrl(rawAvatarUrl);
    }, [latestComment]);

    const latestCommentTimestamp = useMemo(
        () => (latestCommentCreatedAt ? formatTime(latestCommentCreatedAt) : ""),
        [latestCommentCreatedAt, formatTime]
    );
    const latestCommentTimestampLabel = useMemo(
        () => (latestCommentCreatedAt ? formatDateTime(latestCommentCreatedAt) : ""),
        [latestCommentCreatedAt, formatDateTime]
    );
    const latestCommentTimestampDateTime = useMemo(() => {
        if (!latestCommentCreatedAt) {
            return "";
        }
        const createdAtDate = new Date(latestCommentCreatedAt);
        if (Number.isNaN(createdAtDate.getTime())) {
            return "";
        }
        return createdAtDate.toISOString();
    }, [latestCommentCreatedAt]);
    const commentPreviewLabel = t("home.postCard.comments.previewLabel");
    const commentPreviewCta = useMemo(() => (
        totalComments > 0
            ? t("home.postCard.comments.previewCta", { count: totalComments })
            : ""
    ), [t, totalComments]);
    const commentPreviewAria = latestCommentAuthorName
        ? t("home.postCard.comments.previewAria", { author: latestCommentAuthorName })
        : t("home.postCard.comments.previewAria", { author: t("home.postCard.comments.anonymousAuthor") });
    const commentPreviewAvatarAlt = t("home.postCard.comments.previewAvatarAlt", { author: latestCommentAuthorName || t("home.postCard.comments.anonymousAuthor") });
    const latestCommentText = latestComment?.content?.trim() || "";
    const shouldShowCommentPreview = Boolean(
        latestCommentText && latestCommentAuthorName && !isEditing && !showComments && canToggleComments
    );
    const handleShowComments = useCallback(() => {
        setShowComments(true);
    }, []);

    const handleVisibilityChange = async (nextVisibility) => {
        if (!canManagePost) {
            return;
        }
        const normalizedNext = isBandPost ? toBandPostUiVisibility(nextVisibility) : toUiVisibility(nextVisibility);
        if (normalizedNext === visibilityValue) {
            return;
        }
        setIsUpdatingVisibility(true);
        try {
            const updated = await postsService.updatePost(post.id, {
                ...post,
                visibility: normalizedNext,
            });
            onPostMetaChange?.(post.id, {
                visibility: updated.visibility,
                visibilityLabel: updated.visibilityLabel,
                updatedAt: updated.updatedAt,
            });
        } catch (error) {
            console.error("Failed to update visibility", error);
        } finally {
            setIsUpdatingVisibility(false);
        }
    };

    const handleStartEdit = useCallback(() => {
        if (!canManagePost) {
            onToggleDropdown?.(post.id);
            return;
        }
        if (typeof onEditPost === 'function') {
            onToggleDropdown?.(post.id);
            onEditPost(post);
            return;
        }
        if (isSavingEdit) {
            onToggleDropdown?.(post.id);
            return;
        }
        setDraftBody(normalizedBody);
        setIsEditing(true);
        setShowComments(false);
        onToggleDropdown?.(post.id);
    }, [canManagePost, isSavingEdit, normalizedBody, onEditPost, onToggleDropdown, post]);

    const handleCancelEdit = () => {
        if (isSavingEdit) {
            return;
        }
        setIsEditing(false);
        setDraftBody(normalizedBody);
    };

    const handleSaveEdit = async () => {
        if (isSavingEdit) {
            return;
        }
        const nextBody = draftBody.trim();
        if (!nextBody) {
            return;
        }
        if (nextBody === originalTrimmed) {
            setIsEditing(false);
            return;
        }
        setIsSavingEdit(true);
        try {
            const updated = await postsService.updatePost(post.id, {
                ...post,
                body: nextBody,
            });
            setIsEditing(false);
            setDraftBody(normalizePostBody(updated.body));
            onPostMetaChange?.(post.id, {
                body: updated.body,
                visibility: updated.visibility,
                visibilityLabel: updated.visibilityLabel,
                updatedAt: updated.updatedAt,
            });
        } catch (error) {
            console.error("Failed to update post body", error);
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleEditorKeyDown = (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            handleCancelEdit();
            return;
        }
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
            event.preventDefault();
            handleSaveEdit();
        }
    };

    const handleDeleteClick = useCallback(async () => {
        if (onDelete && canManagePost) {
            await onDelete(post.id);
        }
        onToggleDropdown?.(post.id);
        setIsEditing(false);
    }, [canManagePost, onDelete, onToggleDropdown, post.id]);

    const menuActions = useMemo(() => {
        if (!canManagePost) {
            return [];
        }
        return [
            {
                id: 'edit',
                label: t("home.postCard.actions.edit"),
                onSelect: handleStartEdit,
                disabled: isSavingEdit,
            },
            {
                id: 'delete',
                label: t("home.postCard.actions.delete"),
                onSelect: handleDeleteClick,
                disabled: isSavingEdit,
            },
        ];
    }, [canManagePost, handleDeleteClick, handleStartEdit, isSavingEdit, t]);

    const handleReactionToggle = async (emoji, reacted) => {
        if (!canReact) {
            return;
        }
        try {
            const updatedReactions = reacted
                ? await postsService.removeReaction(post.id, emoji)
                : await postsService.addReaction(post.id, emoji);
            setReactions(updatedReactions);
            onPostMetaChange?.(post.id, { reactions: updatedReactions });
        } catch (error) {
            console.error("Failed to update reaction", error);
        }
    };

    const handleEmojiSelect = (emoji) => {
        const value = emoji?.native;
        if (!value) {
            return;
        }
        const existing = reactions.find((reaction) => reaction.emoji === value);
        handleReactionToggle(value, existing?.reacted);
    };

    const handleToggleComments = () => {
        setShowComments((prev) => {
            const next = !prev;
            if (!next) {
                setCommentsExpanded(false);
            }
            return next;
        });
    };

    const loadAllComments = async () => {
        setIsLoadingComments(true);
        try {
            const response = await postsService.fetchComments(post.id, 0, COMMENT_PAGE_SIZE);
            setComments(response.comments);
            setCommentsCount(response.total);
            setCommentsExpanded(true);
            onPostMetaChange?.(post.id, {
                recentComments: trimToRecentThreads(response.comments),
                commentsCount: response.total,
            });
        } catch (error) {
            console.error("Failed to load comments", error);
        } finally {
            setIsLoadingComments(false);
        }
    };

    const collapseComments = () => {
        setComments((post.recentComments || []).slice());
        setCommentsExpanded(false);
    };

    const focusCommentComposer = useCallback(() => {
        window.setTimeout(() => {
            commentComposerRef.current?.focus();
        }, 0);
    }, []);

    const handleStartReply = useCallback((comment) => {
        if (isReadOnly || !comment) {
            return;
        }

        const parentComment = comment.parentId
            ? comments.find((item) => item?.id === comment.parentId)
            : null;
        const target = parentComment || comment;
        if (!target?.id) {
            return;
        }
        const author = target?.author || {};
        const authorName = resolveUserDisplayName(author, t("home.postCard.comments.anonymousAuthor"));
        const authorUsername = resolveUserUsername(author) || authorName;

        setReplyTo({
            commentId: target.id,
            authorName,
            authorUsername,
        });

        if (authorUsername) {
            const mention = `@${authorUsername}`;
            setCommentInput((currentValue) => {
                const current = currentValue || "";
                if (current === mention || current.startsWith(`${mention} `)) {
                    return current;
                }
                return current ? `${mention} ${current}` : `${mention} `;
            });
        }

        setShowComments(true);
        focusCommentComposer();
    }, [comments, focusCommentComposer, isReadOnly, t]);

    const handleCancelReply = useCallback(() => {
        setReplyTo(null);
    }, []);

    const handleSubmitComment = async () => {
        const content = commentInput.trim();
        if (!content || !canComment) {
            return;
        }
        const parentId = replyTo?.commentId ?? null;
        try {
            const newComment = await postsService.addComment(post.id, content, parentId);
            const commentToAdd = newComment && typeof newComment === "object"
                ? {
                    ...newComment,
                    parentId: newComment.parentId ?? parentId,
                }
                : newComment;
            setCommentInput("");
            setReplyTo(null);
            const nextCount = commentsCount + 1;
            setCommentsCount(nextCount);

            const appendedComments = [...comments, commentToAdd];
            const updatedComments = commentsExpanded
                ? appendedComments
                : trimToRecentThreads(appendedComments);
            setComments(updatedComments);

            onPostMetaChange?.(post.id, {
                commentsCount: nextCount,
                recentComments: trimToRecentThreads(appendedComments),
            });
            setShowComments(true);
        } catch (error) {
            console.error("Failed to add comment", error);
        }
    };

    const handleEditComment = useCallback(async (comment, nextContent) => {
        if (isReadOnly || !comment?.id) {
            return;
        }
        const content = (nextContent || "").trim();
        if (!content) {
            return;
        }

        try {
            const updated = await postsService.updateComment(post.id, comment.id, content);
            const updatedComment = updated && typeof updated === "object"
                ? {
                    ...comment,
                    ...updated,
                    parentId: updated.parentId ?? comment.parentId ?? null,
                }
                : {
                    ...comment,
                    content,
                };
            const updatedComments = comments.map((item) => (
                item?.id === comment.id ? { ...item, ...updatedComment } : item
            ));
            setComments(updatedComments);
            onPostMetaChange?.(post.id, {
                recentComments: trimToRecentThreads(updatedComments),
            });
        } catch (error) {
            console.error("Failed to update comment", error);
            throw error;
        }
    }, [comments, isReadOnly, onPostMetaChange, post.id]);

    const handleDeleteComment = useCallback(async (comment) => {
        if (isReadOnly || !comment?.id) {
            return;
        }
        const commentId = String(comment.id);

        try {
            await postsService.deleteComment(post.id, commentId);
            const removedIds = new Set([commentId]);
            const isTopLevelDelete = !comment.parentId;
            if (isTopLevelDelete) {
                comments.forEach((item) => {
                    if (item?.parentId != null && String(item.parentId) === commentId && item?.id != null) {
                        removedIds.add(String(item.id));
                    }
                });
            }

            const updatedComments = comments.filter((item) => (
                item?.id == null || !removedIds.has(String(item.id))
            ));
            let nextComments = updatedComments;
            let nextCount = Math.max(0, commentsCount - removedIds.size);

            if (isTopLevelDelete) {
                try {
                    const response = await postsService.fetchComments(post.id, 0, COMMENT_PAGE_SIZE);
                    const refreshedComments = Array.isArray(response?.comments) ? response.comments : [];
                    if (typeof response?.total === "number") {
                        nextCount = response.total;
                    }
                    if (commentsExpanded) {
                        nextComments = refreshedComments;
                    } else if (refreshedComments.length >= nextCount) {
                        nextComments = trimToRecentThreads(refreshedComments);
                    }
                } catch (refreshError) {
                    console.error("Failed to refresh comments after delete", refreshError);
                }
            }

            setComments(nextComments);
            setCommentsCount(nextCount);
            if (replyTo?.commentId && removedIds.has(String(replyTo.commentId))) {
                setReplyTo(null);
            }
            onPostMetaChange?.(post.id, {
                commentsCount: nextCount,
                recentComments: trimToRecentThreads(nextComments),
            });
        } catch (error) {
            console.error("Failed to delete comment", error);
            throw error;
        }
    }, [comments, commentsCount, commentsExpanded, isReadOnly, onPostMetaChange, post.id, replyTo]);

    const handleCommentKeyDown = (event) => {
        if (event.key === "Escape" && replyTo) {
            event.preventDefault();
            handleCancelReply();
            return;
        }
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSubmitComment();
        }
    };

    return (
        <>
        <FrostedPanel tone="primary" depth="lg" className={cardClassName}>
            <header className="post-card__header">
                <UserCardAvatar
                    userId={post.userId}
                    username={displayName}
                    avatarUrl={avatarUrl}
                    alt={`${displayName || "User"} avatar`}
                    className="post-card__avatar"
                />
                <div className="post-card__profile">
                    <div className="post-card__identity">
                        <span className="post-card__display-name">{displayName}</span>
                        <span
                            className={classNames("post-card__badge", {
                                "post-card__badge--spotlight": isOwner,
                            })}
                        >
                            {isOwner && (
                                <svg
                                    className="post-card__badge-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M12 2l2.6 6.6L22 10l-5.5 4.7L18.2 22 12 18l-6.2 4 1.7-7.3L2 10l7.4-1.4L12 2z" />
                                </svg>
                            )}
                            {isOwner ? t("home.postCard.badges.owner") : t("home.postCard.badges.default")}
                        </span>
                    </div>
                    <div className="post-card__meta">
                        {!editorialEngagement && (
                            <PostVisibilityControl
                                value={visibilityValue}
                                onChange={handleVisibilityChange}
                                disabled={!canManagePost || isUpdatingVisibility || isEditing}
                                size="sm"
                                className={classNames("post-card__visibility-control", {
                                    "is-busy": isUpdatingVisibility,
                                })}
                                onOpenChange={setVisibilityMenuOpen}
                                options={isBandPost ? BAND_POST_VISIBILITY_OPTIONS : undefined}
                            />
                        )}
                        {compactTimestamp && (
                            <>
                                {!editorialEngagement && <span className="post-card__meta-dot" aria-hidden="true" />}
                                <span
                                    className="post-card__timestamp"
                                    title={formattedTimestamp || undefined}
                                    aria-label={timestampLabel || formattedTimestamp || undefined}
                                >
                                    {compactTimestamp}
                                </span>
                            </>
                        )}
                        {relativeTimestamp && (
                            <>
                                <span className="post-card__meta-dot" aria-hidden="true" />
                                <span className="post-card__timestamp post-card__timestamp--relative">
                                    {relativeTimestamp}
                                </span>
                            </>
                        )}
                    </div>
                </div>
                {canManagePost && !hideActionsMenu && (
                    <CardActionsMenu
                        isOpen={isMenuOpen}
                        onToggle={onToggleDropdown}
                        itemId={post.id}
                        actions={menuActions}
                        disabled={isEditing || isSavingEdit}
                    />
                )}
            </header>

            <div className="post-card__content">
                {isEditing ? (
                    <div className="post-card__text post-card__text--editing">
                        <textarea
                            id={editableFieldId}
                            className="post-card__text-input"
                            value={draftBody}
                            onChange={(event) => setDraftBody(event.target.value)}
                            onKeyDown={handleEditorKeyDown}
                            disabled={isSavingEdit}
                            placeholder={t("home.postCard.editor.placeholder")}
                            rows={Math.max(4, Math.min(12, draftBody.split("\n").length + 1))}
                            spellCheck
                            aria-label={t("home.postCard.editor.ariaLabel")}
                            autoFocus
                        />
                        <div className="post-card__editor-actions" role="group" aria-label={t("home.postCard.editor.actionsLabel")}>
                            <IconButton
                                icon={<PostCancelIcon />}
                                label={t("home.postCard.editor.cancelLabel")}
                                variant="ghost"
                                onClick={handleCancelEdit}
                                disabled={isSavingEdit}
                                className="post-card__editor-button"
                            />
                            <IconButton
                                icon={<PostSaveIcon />}
                                label={t("home.postCard.editor.saveLabel")}
                                variant="confirm"
                                onClick={handleSaveEdit}
                                disabled={isSavingEdit || !canSaveEdit}
                                className="post-card__editor-button"
                            />
                        </div>
                    </div>
                ) : (
                    <ContentWithLinkPreviews previewUrls={renderedBody.previews}>
                        <div className="post-card__text" style={{ whiteSpace: "pre-wrap" }}>
                            {renderedBody.content}
                        </div>
                    </ContentWithLinkPreviews>
                )}
                {!isEditing && shouldRenderPrimaryLinkCard && (
                    <PostLinkPreview href={primaryLink} className="post-card__link-preview" />
                )}
                {!isEditing && imageAttachments.length > 0 && (
                    <div className="post-card__attachments" role="group" aria-label="Post attachments">
                        {imageAttachments.map((attachment, index) => {
                            const handleClick = (event) => {
                                if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1)) {
                                    return;
                                }
                                event?.preventDefault?.();
                                if (attachmentItems.length) {
                                    openWithItems(attachmentItems, index);
                                }
                            };
                            const alt = attachment?.contentType ? `${attachment.contentType} attachment` : 'Post attachment';
                            return (
                                <button
                                    type="button"
                                    key={attachment.id || attachment.url}
                                    className="post-card__attachment"
                                    onClick={handleClick}
                                >
                                    <img src={attachment.displayUrl} alt={alt} loading="lazy" />
                                </button>
                            );
                        })}
                    </div>
                )}
                {!isEditing && (
                    editorialEngagement ? (() => {
                        const likeEmoji = "❤️";
                        const likeEntry = reactions.find((r) => r?.emoji === likeEmoji) || null;
                        const likedNow = (() => {
                            if (!likeEntry) return false;
                            if (typeof likeEntry.reacted === "boolean") return likeEntry.reacted;
                            if (!viewerId || !Array.isArray(likeEntry.users)) return false;
                            return likeEntry.users.some((u) => String(u) === viewerId);
                        })();
                        const likeCount = Number(likeEntry?.count) || 0;
                        return (
                            <div className="post-card__engagement post-card__engagement--editorial">
                                <LikeButton
                                    liked={likedNow}
                                    count={likeCount}
                                    disabled={!canReact}
                                    onToggle={() => handleReactionToggle(likeEmoji, likedNow)}
                                    likeLabel={t("home.postComments.actions.like")}
                                    likedLabel={t("home.postComments.actions.liked")}
                                />
                                {canToggleComments && (
                                    <CommentToggle
                                        count={commentsCount}
                                        active={showComments}
                                        onToggle={handleToggleComments}
                                    />
                                )}
                                {canManagePost && (
                                    <div className="post-card__engagement-visibility">
                                        <PostVisibilityControl
                                            value={visibilityValue}
                                            onChange={handleVisibilityChange}
                                            disabled={isUpdatingVisibility || isEditing}
                                            size="sm"
                                            className={classNames("post-card__visibility-control", {
                                                "is-busy": isUpdatingVisibility,
                                            })}
                                            onOpenChange={setVisibilityMenuOpen}
                                            options={isBandPost ? BAND_POST_VISIBILITY_OPTIONS : undefined}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })() : (
                        <PostEngagement
                            className="post-card__engagement"
                            reactions={reactions}
                            canReact={canReact}
                            onToggleReaction={handleReactionToggle}
                            onEmojiSelect={handleEmojiSelect}
                            commentsCount={commentsCount}
                            canToggleComments={canToggleComments}
                            showComments={showComments}
                            onToggleComments={handleToggleComments}
                        />
                    )
                )}
                {shouldShowCommentPreview && (
                    <CommentPreview
                        data-testid="post-card-comment-preview"
                        className="post-card__comment-preview"
                        label={commentPreviewLabel}
                        userId={latestCommentAuthorId}
                        authorName={latestCommentAuthorName}
                        commentText={latestCommentText}
                        avatarUrl={latestCommentAvatarUrl}
                        avatarAlt={commentPreviewAvatarAlt}
                        timestamp={latestCommentTimestamp}
                        timestampTitle={latestCommentTimestampLabel}
                        timestampDateTime={latestCommentTimestampDateTime}
                        cta={commentPreviewCta}
                        ariaLabel={commentPreviewAria}
                        onClick={handleShowComments}
                    />
                )}
            </div>

            <PostComments
                isVisible={!isEditing && showComments}
                comments={comments}
                commentsCount={commentsCount}
                isExpanded={commentsExpanded}
                isLoading={isLoadingComments}
                canComment={canComment}
                commentValue={commentInput}
                onCommentChange={setCommentInput}
                onCommentKeyDown={handleCommentKeyDown}
                onSubmitComment={handleSubmitComment}
                onStartReply={isReadOnly ? null : handleStartReply}
                onEditComment={isReadOnly ? null : handleEditComment}
                onDeleteComment={isReadOnly ? null : handleDeleteComment}
                replyTo={replyTo}
                onCancelReply={handleCancelReply}
                composerRef={commentComposerRef}
                onLoadAll={isReadOnly ? null : loadAllComments}
                onCollapse={collapseComments}
                postAuthorId={ownerId}
                currentUserId={isReadOnly ? null : viewerId}
                readOnly={isReadOnly}
                className="post-card__comments"
            />
            {lightboxEl}
        </FrostedPanel>
        </>
    );
}

PostCard.propTypes = {
    post: PropTypes.shape({
        id: PropTypes.string.isRequired,
        body: PropTypes.string,
        reactions: PropTypes.array,
        recentComments: PropTypes.array,
        commentsCount: PropTypes.number,
        visibility: PropTypes.string,
        createdAt: PropTypes.string,
        author: PropTypes.shape({
            displayName: PropTypes.string,
            username: PropTypes.string,
            profilePicture: PropTypes.string,
        }),
        attachments: PropTypes.array,
        canReact: PropTypes.bool,
        canComment: PropTypes.bool,
        canManage: PropTypes.bool,
        userId: PropTypes.string,
        ownedByViewer: PropTypes.bool,
    }).isRequired,
    currentUserId: PropTypes.string,
    dropdownOpenId: PropTypes.string,
    onToggleDropdown: PropTypes.func,
    onDelete: PropTypes.func,
    onPostMetaChange: PropTypes.func,
    onEditPost: PropTypes.func,
    defaultShowComments: PropTypes.bool,
    editorialEngagement: PropTypes.bool,
    readOnly: PropTypes.bool,
    hideActionsMenu: PropTypes.bool,
};

PostCard.defaultProps = {
    currentUserId: null,
    dropdownOpenId: null,
    onToggleDropdown: null,
    onDelete: null,
    onPostMetaChange: null,
    onEditPost: null,
    readOnly: false,
};
